import { useState, type ReactNode } from 'react';
import { ASSIGNABLE_ROLE_KEYS, BLANK_ROLE_START, type CreateRoleInput, type RoleKey } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select, type SelectOption } from '../../../components/Select';
import { useToasts } from '../../../stores/toasts';
import { useCreateRole, type RoleSummary } from '../api/roles-api';
import styles from './NewRoleForm.module.css';

/**
 * "Copy a role" (ADR 0085; Admin redesign 2026-11-02, Roles A). Opens beside its button. Blank is
 * the first choice: a role unlike any other starts with nothing, and the Admin picks what it may
 * do. Then every role, custom ones included: a copy starts with everything the original can do.
 */

type StartFrom = CreateRoleInput['startFrom'];

const isAssignable = (key: RoleKey | null): boolean => key !== null && (ASSIGNABLE_ROLE_KEYS as readonly RoleKey[]).includes(key);

/** A standard role is copied by its key (as before); any other live role by its id. */
function startValueOf(role: RoleSummary): StartFrom {
  return role.isSystem && isAssignable(role.key) ? (role.key as StartFrom) : role.id;
}

export const BLANK_START_LABEL = 'Blank — no access anywhere';

export function startFromOptions(roles: readonly RoleSummary[]): readonly SelectOption[] {
  return [
    { value: BLANK_ROLE_START, label: BLANK_START_LABEL, description: 'Then pick only what this role needs.' },
    ...roles
      .filter((role) => role.key !== 'candidate')
      .map((role) => ({
        value: startValueOf(role),
        label: role.label,
        description: role.isSystem ? 'Standard role' : role.copiedFromLabel ? `Copy of ${role.copiedFromLabel}` : 'Started blank',
      })),
  ];
}

export function NewRoleForm({
  roles,
  onCancel,
  onCreated,
}: {
  readonly roles: readonly RoleSummary[];
  readonly onCancel: () => void;
  readonly onCreated: (id: string) => void;
}): ReactNode {
  const create = useCreateRole();
  const showToast = useToasts((state) => state.show);
  const [label, setLabel] = useState('');
  const [startFrom, setStartFrom] = useState<StartFrom>(BLANK_ROLE_START);
  const options = startFromOptions(roles);
  const isBlank = startFrom === BLANK_ROLE_START;
  const canSubmit = label.trim().length >= 2;

  const submit = (): void => {
    create.mutate(
      { label: label.trim(), description: null, startFrom },
      {
        onSuccess: (created) => {
          showToast({ tone: 'success', message: `Role "${created.label}" created` });
          setLabel('');
          onCreated(created.id);
        },
        onError: (error) => {
          if (!error.fields.label) showToast({ tone: 'danger', message: error.message });
        },
      },
    );
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) submit();
      }}
    >
      <h2 className={styles.title}>Copy a role</h2>
      <p className={styles.lede}>
        {isBlank
          ? 'A blank role starts with no access anywhere. Then pick what it needs.'
          : 'The copy starts with everything the original can do. Then take away what it should not have.'}
      </p>

      <Field label="Start from" isRequired>
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Start from"
            value={startFrom}
            onChange={(value) => setStartFrom(options.find((option) => option.value === value)?.value ?? BLANK_ROLE_START)}
            options={options}
          />
        )}
      </Field>

      <Field label="Name the new role" hint="Something a colleague would recognise, like “Front desk”." error={create.error?.fields.label} isRequired>
        {({ inputId, isInvalid }) => (
          <Input
            id={inputId}
            value={label}
            autoFocus
            maxLength={80}
            onChange={(event) => setLabel(event.target.value)}
            isInvalid={isInvalid}
            placeholder="Front desk"
          />
        )}
      </Field>

      <div className={styles.actions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" isPending={create.isPending} disabled={!canSubmit}>
          {isBlank ? 'Create the role' : 'Make the copy'}
        </Button>
      </div>
    </form>
  );
}
